import {defineMessages} from "react-intl";

export type DeviceType = "provisioned" | "client-mobile" | "client-desktop" | "thirdparty" | "linked";

export const deviceTranslations = defineMessages<DeviceType>({
    provisioned: {
        id: "provisioned",
        defaultMessage: "Provisioned",
    },
    "client-mobile": {
        id: "client-mobile",
        defaultMessage: "Mobile client",
    },
    "client-desktop": {
        id: "client-desktop",
        defaultMessage: "Desktop client",
    },
    thirdparty: {
        id: "thirdparty",
        defaultMessage: "Third-Party",
    },
    linked: {
        id: "linked",
        defaultMessage: "Linked",
    },
});

export interface DeviceLimitItem {
    devices: DeviceType[];
    limit?: number | null;
}

export type DeviceLimitInfo = {
    used: number;
    remaining: number | "unlimited";
};

export type DevicesInfo = Record<DeviceType, DeviceLimitInfo>;
export type DeviceSetLimitSummary = {
    perDevice: Record<DeviceType, DeviceLimitInfo>;
    remainingLimits: DeviceLimitItem[];
};

export type DevicesUsedInfo = Record<DeviceType, number>;

/**
 * Get the summary of the device limits and the remaining limits for the user.
 * The `perDevice.remaining` field is only an approximation, and is only accurate if the user
 * wants to add that many devices ONLY of that type, and does not take the other device types into account
 * in the limit.
 *
 * @param deviceLimits The device limits for the user
 * @param devicesUsed The devices used by the user for each device type
 * @returns The summary of the device limits and remaining limits
 **/
export const getDeviceSetLimitSummary = (
    deviceLimits: DeviceLimitItem[],
    devicesUsed: DevicesUsedInfo,
): DeviceSetLimitSummary => {
    const devicesInfo: DevicesInfo = {
        provisioned: {used: devicesUsed.provisioned, remaining: 0},
        "client-mobile": {used: devicesUsed["client-mobile"], remaining: 0},
        "client-desktop": {used: devicesUsed["client-desktop"], remaining: 0},
        thirdparty: {used: devicesUsed.thirdparty, remaining: 0},
        linked: {used: devicesUsed.linked, remaining: 0},
    };

    const remainingLimits = structuredClone(deviceLimits);
    remainingLimits.sort((a, b) => {
        return a.devices.length - b.devices.length;
    });

    // let's go through the limits, and assign the remaining values to its device types
    for (const limit of remainingLimits) {
        if (limit.limit == null) {
            // if the limit is unlimited, then we can just set the remaining to unlimited for all its device types
            for (const device of limit.devices) {
                devicesInfo[device].remaining = "unlimited";
            }
        } else {
            for (const device of limit.devices) {
                const info = devicesInfo[device];

                // if the remaining is already unlimited, then we can skip this limit
                if (info.remaining === "unlimited") {
                    continue;
                }

                const used = devicesUsed[device];
                if (limit.limit >= used) {
                    // if the user has more quota than used devices of this type, then we can just
                    // subtract the used devices from the limit, and add it to the remaining
                    limit.limit -= used;

                    // reset the used devices for this type, so that it will not be subtracted from the other device types
                    devicesUsed[device] = 0;

                    if (info.remaining > 0) info.remaining += limit.limit;
                    else info.remaining = limit.limit;

                    // we must also subtract the used devices from the other device types that share the same limit
                    const other_device_types = limit.devices.filter((d) => d !== device);
                    for (const other_device of other_device_types) {
                        const otherInfo = devicesInfo[other_device];
                        if (otherInfo.remaining === "unlimited") continue;
                        if (otherInfo.remaining >= used) {
                            otherInfo.remaining -= used;
                        } else {
                            otherInfo.remaining = 0;
                        }
                    }
                } else {
                    // otherwise the user has ran out of this particular limit
                    devicesUsed[device] = used - limit.limit;
                    limit.limit = 0;
                }
            }
        }
    }

    return {
        perDevice: devicesInfo,
        remainingLimits: remainingLimits.filter((limit) => limit.limit !== 0),
    };
};
